import StatusChip from 'components/@extended/StatusChip.tsx';
import AuditInfo from 'components/audit/AuditInfo';
import { DraftSharedWith } from 'components/drafts/DraftSharedWith';
import { DraftActionsContext } from 'components/drafts/useDraftActions';
import { withMissing } from 'components/drafts/withMissing';
import GridItemList, { infoLabel, Row } from 'components/GridItemList';
import Loader from 'components/Loader.tsx';
import { RECORD_GRID_CLASS } from 'components/record-header/RecordHeader';
import CapFeeBudgetBox from 'pages/invoices/layout/views/components/CapFeeBudgetBox';
import { FormattedDate } from 'components/misc/FormattedDate';
import MoneyDisplay from 'components/util/MoneyDisplay';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import React, { useContext, useMemo } from 'react';
import { TotalHours } from 'sections/stats/Stats.tsx';
import { useGetMinOfficesWithFiltersQuery } from 'store/api/offices.ts';
import { MatterEmployeeRoleType, MatterListItem, RemunerationCalcType } from 'types/matters.ts';
import { OfficeMin } from 'types/offices.ts';
import { NamedEntityMinDto } from 'types/other.ts';
import { ProjectDetailsDto } from 'types/projects';
import { FeatureFlag } from 'types/features.ts';
import { t } from 'utils/getTranslations';
import { useHasPermissions } from 'utils/permissionUtil';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags.ts';
import { getCaseChips, getLinks } from '../../matters/util.tsx';
import { ProjectSection } from '../form-sections/ProjectSection';

interface Props {
    project: ProjectDetailsDto;
}

const GeneralView: React.FC<Props> = ({ project }) => {
    const isNew = useIsNewDesign();
    // On a draft, the values it was saved without read as missing, and whom it is shared with shows.
    const draftActions = useContext(DraftActionsContext);
    const missingFields = draftActions?.draft?.missingFields;
    const sharedWith = draftActions ? <DraftSharedWith /> : null;
    const formLabels = isNew || !!draftActions;
    // What the Billing section's form asks before it draws a field: a row whose field it leaves out stays as text.
    const hideDontNullifyOption = useIsFeatureEnabled(FeatureFlag.HIDE_DONT_NULLIFY_OPTION);
    const isRemunerationCalcTypeControlEnabled = useIsFeatureEnabled(FeatureFlag.REMUNERATION_CALC_TYPE_CONTROL);
    const hasPermissionFieldAccessRemunerationTime = useHasPermissions(['FIELD_ACCESS.TIMECARD.REMUNERATION_TIME']);
    const { data: officeListData, isLoading: isLoadingOfficeList } = useGetMinOfficesWithFiltersQuery({
        page: 0,
        pageSize: 2000,
    });

    const distinctProjectOffices = useMemo(() => {
        const distinctOffices = new Map<number, OfficeMin>();
        for (const matter of project.matters ?? []) {
            const office = matter.handledByOffice;
            if (office != null && !distinctOffices.has(office.id)) {
                distinctOffices.set(office.id, office);
            }
        }
        return Array.from(distinctOffices.values());
    }, [project.matters]);

    const officeListSorted = useMemo(() => {
        const offices =
            officeListData?.content.map((office) => ({
                ...office,
                matched: distinctProjectOffices.some((projectOffice) => projectOffice.id === office.id),
            })) ?? [];
        return offices.sort((a, b) => {
            if (a.matched !== b.matched) {
                return a.matched ? -1 : 1;
            }
            if (a.abbreviation < b.abbreviation) return -1;
            if (a.abbreviation > b.abbreviation) return 1;
            return 0;
        });
    }, [distinctProjectOffices, officeListData?.content]);

    function getDistinctEmployees(matters: MatterListItem[] | undefined): NamedEntityMinDto[] {
        if (!matters) return [];

        const allEmployees = matters
            .flatMap((matter) => [
                matter.responsibleEmployee,
                matter.supervisedByEmployee,
                ...(matter.employees
                    ?.filter(
                        (emp) => emp.matterEmployeeRoleTypeCode !== MatterEmployeeRoleType.PAYMENT_NOTIFICATION_RECEIVER
                    )
                    .map((emp) => emp.employee) ?? []),
            ])
            .filter((employee): employee is NamedEntityMinDto => !!employee && !!employee.id);

        const distinctEmployees = new Map<number, NamedEntityMinDto>();
        for (const employee of allEmployees) {
            if (!distinctEmployees.has(employee.id)) {
                distinctEmployees.set(employee.id, employee);
            }
        }
        return Array.from(distinctEmployees.values());
    }

    // The new design's rows carry their field's label and come in the form's order; classic keeps its own labels
    // but on a draft, whose missing fields go by the form's.
    const openedDate: Row = {
        label: t(formLabels ? 'projects.form.opened-date' : 'projects.general.overview-tab.opened-date'),
        content: <FormattedDate value={project.openedDate} />,
    };
    const closedDate: Row = {
        label: t(formLabels ? 'projects.form.closed-date' : 'projects.general.overview-tab.closed-date'),
        content: <FormattedDate value={project.closedDate} />,
    };
    const offices: Row = {
        label: t('projects.general.overview-tab.offices'),
        wide: true,
        readOnly: true,
        content: !isLoadingOfficeList ? (
            <span className="flex flex-row flex-wrap gap-1">
                {officeListSorted?.map((office) => (
                    <StatusChip
                        key={office.id}
                        label={office.abbreviation}
                        type={office.matched ? 'neutral' : 'inactive'}
                    />
                )) || '-'}
            </span>
        ) : (
            <Loader />
        ),
    };
    const description: Row = {
        label: t(formLabels ? 'projects.form.description' : 'projects.general.overview-tab.description'),
        content: project.description || '-',
        multiline: true,
    };
    const getOverviewData = (): Row[] =>
        isNew
            ? [
                  { label: t('projects.form.name'), content: project.projectName || '-' },
                  description,
                  openedDate,
                  closedDate,
                  offices,
              ]
            : [openedDate, closedDate, offices, description];

    const getBillingData = (): Row[] => [
        {
            content: project.isNonBillable ? t('yes') : t('no'),
            label: t('projects.non-billable'),
        },
        {
            content: project.preserveNonBillableHours ? t('yes') : t('no'),
            // With its field's info icon at the end of the line, so the label wraps where the field's does.
            label: isNew
                ? infoLabel(
                      'matters.preserve-non-billable-hours',
                      'matters.tooltips.preserve-non-billable-hours',
                      'justify-between'
                  )
                : t('matters.preserve-non-billable-hours'),
            // The form has the field on a non-billable project only.
            hidden: isNew && !project.isNonBillable,
            readOnly: hideDontNullifyOption,
        },
        {
            content: project.remunerationCalcTypeCode === RemunerationCalcType.EQUALS_USED_TIME ? t('yes') : t('no'),
            label: t('matters.use-used-time-for-remuneration'),
            readOnly: !hasPermissionFieldAccessRemunerationTime || !isRemunerationCalcTypeControlEnabled,
        },
    ];

    const getEmployeesData = (): Row[] => [
        {
            label: t(formLabels ? 'projects.form.supervised-by' : 'projects.general.employees-tab.supervised-by'),
            content: getLinks('employee', project.supervisedByEmployee ? [project.supervisedByEmployee] : []),
        },
        {
            label: t(
                formLabels ? 'projects.form.responsible-lawyer' : 'projects.general.employees-tab.responsible-lawyer'
            ),
            content: getLinks('employee', project.responsibleEmployee ? [project.responsibleEmployee] : []),
        },
        {
            label: t('projects.general.employees-tab.employees'),
            content: getLinks('employee', getDistinctEmployees(project.matters)),
            wide: true,
            readOnly: true,
        },
    ];

    const auditInfo = (
        <AuditInfo
            createdAt={project.createdAt}
            createdByEmployee={project.createdByEmployee}
            lastModifiedAt={project.lastModifiedAt}
            changedByEmployee={project.changedByEmployee}
        />
    );

    if (isNew) {
        const overviewRows = withMissing(getOverviewData(), missingFields);
        const billingRows = getBillingData();
        const teamRows = withMissing(getEmployeesData(), missingFields);
        // As the case's General tab: the cards stacked on the left, the figures on the right.
        return (
            <div className={RECORD_GRID_CLASS}>
                <div className="flex min-w-0 flex-col gap-(--card-gap)">
                    <ProjectSection
                        id="overview"
                        titleId="projects.general.overview-tab.heading"
                        project={project}
                        rows={overviewRows}
                    >
                        {(edit) => (
                            <GridItemList
                                isNew
                                titleId="projects.general.overview-tab.heading"
                                data={overviewRows}
                                button={edit}
                            />
                        )}
                    </ProjectSection>
                    <ProjectSection id="billing" titleId="projects.form.billing" project={project} rows={billingRows}>
                        {(edit) => (
                            <GridItemList isNew titleId="projects.form.billing" data={billingRows} button={edit} />
                        )}
                    </ProjectSection>
                    <ProjectSection
                        id="team"
                        titleId="projects.general.employees-tab.heading"
                        project={project}
                        rows={teamRows}
                    >
                        {(edit) => (
                            <GridItemList
                                isNew
                                titleId="projects.general.employees-tab.heading"
                                data={teamRows}
                                button={edit}
                            />
                        )}
                    </ProjectSection>
                    <ProjectSection id="cap" titleId="projects.form.project-cap" project={project}>
                        {(edit) => (
                            <GridItemList
                                isNew
                                titleId="projects.form.project-cap"
                                data={withMissing(
                                    [
                                        {
                                            label: t('projects.form.cap-limit'),
                                            content:
                                                project.capLimit != null ? (
                                                    <MoneyDisplay amount={project.capLimit} />
                                                ) : (
                                                    '-'
                                                ),
                                        },
                                        {
                                            label: t('projects.form.cap-currency'),
                                            content: project.capCurrencyTypeCode || '-',
                                        },
                                    ],
                                    missingFields
                                )}
                                button={edit}
                            />
                        )}
                    </ProjectSection>
                    <ProjectSection id="cases" titleId="projects.form.connected-cases" project={project}>
                        {(edit) => (
                            <GridItemList
                                isNew
                                titleId="projects.form.connected-cases"
                                data={[
                                    {
                                        label: t('projects.form.connected-cases'),
                                        content: '-',
                                        // One chip a case, as the field has them, each still the case's link.
                                        chips: getCaseChips(project.matters ?? []),
                                    },
                                ]}
                                button={edit}
                            />
                        )}
                    </ProjectSection>
                    {auditInfo}
                </div>
                <div className="min-w-0">
                    {sharedWith ? <div className="mb-(--card-gap)">{sharedWith}</div> : null}
                    <TotalHours
                        financialData={project.financialData}
                        className="mb-(--card-gap) rounded-[10px]"
                        contentClassName="px-5 py-4"
                    />
                    <CapFeeBudgetBox
                        className="rounded-[10px]"
                        contentClassName="px-4 py-3"
                        variant="outlined"
                        budget={project.projectStats?.capLimitCapCurrency ?? 0}
                        remaining={project.projectStats?.remainingCapCurrency ?? 0}
                        used={project.projectStats?.totalCapCurrency ?? 0}
                        currency={project.projectStats?.capCurrencyTypeCode ?? ''}
                        financialData={project.financialData}
                    />
                </div>
            </div>
        );
    }

    return (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-12">
            <div className="sm:col-span-2">
                <TotalHours financialData={project.financialData} className="mb-4" />
                <CapFeeBudgetBox
                    className="rounded-[7px]"
                    variant="outlined"
                    budget={project.projectStats?.capLimitCapCurrency ?? 0}
                    remaining={project.projectStats?.remainingCapCurrency ?? 0}
                    used={project.projectStats?.totalCapCurrency ?? 0}
                    currency={project.projectStats?.capCurrencyTypeCode ?? ''}
                    financialData={project.financialData}
                />
            </div>
            <div className="sm:col-span-5">
                <GridItemList
                    titleId="projects.general.overview-tab.heading"
                    size={12}
                    data={withMissing([...getOverviewData(), ...getBillingData()], missingFields)}
                    key="project-overview"
                />
                <div className="mt-4">{auditInfo}</div>
            </div>
            <div className="flex flex-col gap-6 sm:col-span-5">
                {sharedWith}
                <GridItemList
                    titleId="projects.general.employees-tab.heading"
                    size={12}
                    data={withMissing(getEmployeesData(), missingFields)}
                    key="project-team"
                />
            </div>
        </div>
    );
};

export default GeneralView;
